import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdmin } from '@/contexts/AdminContext';

type GenderOption = { en: string; ko: string; value: string };

const genderOptions: GenderOption[] = [
  { en: 'Male', ko: '남성', value: 'male' },
  { en: 'Female', ko: '여성', value: 'female' },
  { en: 'Non-binary', ko: '논바이너리', value: 'non-binary' },
  { en: 'Prefer not to say', ko: '밝히고 싶지 않음', value: 'prefer-not-to-say' },
];

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const { isAdmin, user, isLoading, login, signup, logout } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authModal, setAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState('');
  const [source, setSource] = useState('');
  const [nationality, setNationality] = useState('');
  const [birth, setBirth] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('Home', '홈') },
    { path: '/about', label: t('About', '소개') },
    { path: '/issues', label: t('Posts', '게시물') },
  ];

  const adminLinks = isAdmin
    ? [{ path: '/admin/users', label: lang === 'ko' ? '관리자 대시보드' : 'Admin Dashboard' }]
    : [];

  const allNavLinks = [...navLinks, ...adminLinks];

  const resetAuthForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setGender('');
    setSource('');
    setNationality('');
    setBirth('');
    setAuthError('');
    setAuthLoading(false);
  };

  const handleSharePage = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setAuthError(t('Please fill in all fields.', '모든 항목을 입력해주세요.'));
      return;
    }
    setAuthLoading(true);
    const result = await login(email.trim(), password);
    setAuthLoading(false);
    if (result.success) {
      setAuthModal(false);
      resetAuthForm();
    } else {
      setAuthError(result.error || t('Login failed.', '로그인에 실패했습니다.'));
    }
  };

  const handleSignup = async () => {
    if (!email.trim() || !password.trim() || !displayName.trim()) {
      setAuthError(t('Please fill in all fields.', '모든 항목을 입력해주세요.'));
      return;
    }
    if (password.length < 6) {
      setAuthError(t('Password must be at least 6 characters.', '비밀번호는 6자 이상이어야 합니다.'));
      return;
    }
    setAuthLoading(true);
    const result = await signup(
      email.trim(),
      password,
      displayName.trim(),
      gender || undefined,
      source.trim() || undefined,
      nationality.trim() || undefined,
      birth || undefined
    );
    setAuthLoading(false);
    if (result.success) {
      setAuthModal(false);
      resetAuthForm();
    } else {
      setAuthError(result.error || t('Signup failed.', '회원가입에 실패했습니다.'));
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || isAdmin
            ? 'bg-stone-900 border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        {isAdmin && (
          <div className="bg-stone-800 text-white text-[10px] text-center py-0.5 font-semibold tracking-wide">
            {lang === 'ko' ? '관리자 모드 — 모든 관리 기능에 접근할 수 있습니다' : 'Admin Mode — Full access to all management features'}
          </div>
        )}
        <nav className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/87a8138ca64b05ac1815bceaa72b9a44/42531b770a6962e3374af2e6eb919d1e.png"
                alt="LUVing Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className={`text-sm font-bold tracking-tight transition-colors ${scrolled || isAdmin ? 'text-white' : 'text-white'}`}>
                LUVing
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {allNavLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap
                  ${location.pathname === path
                    ? scrolled || isAdmin ? 'text-white border-b border-white' : 'text-white border-b border-white'
                    : scrolled || isAdmin ? 'text-white/70 hover:text-white' : 'text-white/70 hover:text-white'
                  }`}
              >
                {path === '/admin/users' && (
                  <i className="ri-shield-check-line mr-1 text-xs" />
                )}
                {label}
              </Link>
            ))}

            <div className="flex items-center border border-stone-200/40 rounded-full p-0.5 bg-white/10 backdrop-blur-sm">
              <button
                onClick={() => setLang('en')}
                className={`text-xs px-3 py-1 rounded-full cursor-pointer transition-all whitespace-nowrap font-medium
                  ${lang === 'en' ? 'bg-white text-stone-900' : scrolled || isAdmin ? 'text-white/70 hover:text-white' : 'text-white/70 hover:text-white'}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang('ko')}
                className={`text-xs px-3 py-1 rounded-full cursor-pointer transition-all whitespace-nowrap font-medium
                  ${lang === 'ko' ? 'bg-white text-stone-900' : scrolled || isAdmin ? 'text-white/70 hover:text-white' : 'text-white/70 hover:text-white'}`}
              >
                한국어
              </button>
            </div>

            {/* Share button - desktop */}
            <button
              onClick={handleSharePage}
              title={copiedLink ? (lang === 'ko' ? '복사됨' : 'Copied') : (lang === 'ko' ? '링크 복사' : 'Copy link')}
              className="hidden md:flex w-8 h-8 items-center justify-center rounded-full border border-white/30 text-white/60 hover:text-white hover:border-white/50 cursor-pointer transition-colors"
            >
              <i className={`text-sm ${copiedLink ? 'ri-check-line' : 'ri-share-line'}`} />
            </button>

            {!isLoading && (
              <>
                {user ? (
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <span className="text-[10px] px-2 py-0.5 bg-stone-800 text-white rounded-full font-semibold whitespace-nowrap">
                        <i className="ri-shield-check-line mr-1" />Admin
                      </span>
                    )}
                    <span className={`text-xs font-medium whitespace-nowrap ${scrolled || isAdmin ? 'text-white/80' : 'text-white/80'}`}>
                      {user.displayName || user.email.split('@')[0]}
                    </span>
                    <button
                      onClick={logout}
                      className={`text-xs px-2.5 py-1 rounded-full border cursor-pointer whitespace-nowrap transition-colors
                        ${scrolled || isAdmin ? 'border-white/30 text-white/50 hover:text-white' : 'border-white/30 text-white/50 hover:text-white'}`}
                    >
                      {t('Logout', '로그아웃')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAuthTab('login'); setAuthModal(true); }}
                    className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors
                      ${scrolled || isAdmin ? 'border-white/30 text-white/60 hover:text-white' : 'border-white/30 text-white/60 hover:text-white'}`}
                  >
                    {t('Login', '로그인')}
                  </button>
                )}
              </>
            )}
          </div>

          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer transition-colors ${scrolled || isAdmin ? 'text-white' : 'text-white'}`}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </nav>

        {menuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 space-y-3">
            {allNavLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`block text-sm font-medium py-1 cursor-pointer ${path === '/admin/users' ? 'text-amber-700' : 'text-stone-700 hover:text-stone-900'}`}
              >
                {path === '/admin/users' && (
                  <i className="ri-shield-check-line mr-1" />
                )}
                {label}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t border-stone-50">
              <button onClick={() => setLang('en')} className={`text-sm cursor-pointer ${lang === 'en' ? 'font-bold text-stone-800' : 'text-stone-400'}`}>EN</button>
              <span className="text-stone-200">|</span>
              <button onClick={() => setLang('ko')} className={`text-sm cursor-pointer ${lang === 'ko' ? 'font-bold text-stone-800' : 'text-stone-400'}`}>한국어</button>
            </div>

            {/* Share button - mobile */}
            <div className="flex items-center gap-3 pt-2 border-t border-stone-50">
              <button
                onClick={handleSharePage}
                className="flex items-center gap-1.5 text-sm text-stone-600 cursor-pointer"
              >
                <i className={`text-sm ${copiedLink ? 'ri-check-line text-teal-600' : 'ri-share-line'}`} />
                {copiedLink ? (lang === 'ko' ? '링크 복사됨' : 'Link copied') : (lang === 'ko' ? '페이지 공유' : 'Share page')}
              </button>
            </div>

            {!isLoading && (
              <div className="pt-2 border-t border-stone-50">
                {user ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-600">{user.displayName || user.email}</span>
                      {isAdmin && (
                        <span className="text-[10px] px-2 py-0.5 bg-stone-800 text-white rounded-full font-semibold">
                          Admin
                        </span>
                      )}
                    </div>
                    <button onClick={logout} className="text-sm text-red-500 cursor-pointer">{t('Logout', '로그아웃')}</button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAuthTab('login'); setAuthModal(true); setMenuOpen(false); }}
                    className="text-sm text-stone-600 cursor-pointer"
                  >
                    {t('Login / Sign Up', '로그인 / 회원가입')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {/* Auth Modal */}
      {authModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden max-h-[85vh] flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-stone-100 shrink-0">
              <button
                onClick={() => { setAuthTab('login'); setAuthError(''); }}
                className={`flex-1 py-3 text-sm font-semibold cursor-pointer transition-colors ${authTab === 'login' ? 'text-stone-800 border-b-2 border-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
              >
                {t('Login', '로그인')}
              </button>
              <button
                onClick={() => { setAuthTab('signup'); setAuthError(''); }}
                className={`flex-1 py-3 text-sm font-semibold cursor-pointer transition-colors ${authTab === 'signup' ? 'text-stone-800 border-b-2 border-stone-800' : 'text-stone-400 hover:text-stone-600'}`}
              >
                {t('Sign Up', '회원가입')}
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {authTab === 'signup' && (
                <>
                  {/* User Name — Required */}
                  <div>
                    <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                      {t('User Name', '사용자 이름')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder={t('Your name', '이름을 입력하세요')}
                      className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                    />
                  </div>
                </>
              )}

              {/* Email */}
              <div>
                <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                  {t('Email', '이메일')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                  {t('Password', '비밀번호')} <span className="text-red-400">*</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('At least 6 characters', '6자 이상')}
                  className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                />
              </div>

              {authTab === 'signup' && (
                <>
                  {/* Divider: Optional Info */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-stone-100" />
                    <span className="text-[10px] text-stone-400 font-medium whitespace-nowrap">
                      {t('Optional Information', '선택 정보')}
                    </span>
                    <div className="flex-1 border-t border-stone-100" />
                  </div>

                  {/* Source — How did you hear about us? */}
                  <div>
                    <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                      {t('How did you hear about us?', '이 사이트를 어떻게 알게 되셨나요?')} <span className="text-[10px] text-stone-400 font-normal">({t('optional', '선택')})</span>
                    </label>
                    <input
                      type="text"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      placeholder={t('Instagram, friend, search engine...', '인스타그램, 친구, 검색 엔진...')}
                      className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                    />
                  </div>

                  {/* Nationality */}
                  <div>
                    <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                      {t('Nationality', '국적')} <span className="text-[10px] text-stone-400 font-normal">({t('optional', '선택')})</span>
                    </label>
                    <input
                      type="text"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                      placeholder={t('Country', '국가')}
                      className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400"
                    />
                  </div>

                  {/* Birth */}
                  <div>
                    <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                      {t('Birth', '생년월일')} <span className="text-[10px] text-stone-400 font-normal">({t('optional', '선택')})</span>
                    </label>
                    <input
                      type="text"
                      value={birth}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        let formatted = '';
                        if (raw.length <= 2) {
                          formatted = raw;
                        } else if (raw.length <= 4) {
                          formatted = raw.slice(0, 2) + '/' + raw.slice(2);
                        } else {
                          formatted = raw.slice(0, 2) + '/' + raw.slice(2, 4) + '/' + raw.slice(4, 8);
                        }
                        setBirth(formatted);
                      }}
                      placeholder="MM/DD/YYYY"
                      maxLength={10}
                      className="w-full text-sm px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:border-stone-400 text-stone-600"
                    />
                    <p className="text-[10px] text-stone-400 mt-1">
                      {lang === 'ko' ? '예: 08/15/1995' : 'e.g. 08/15/1995'}
                    </p>
                  </div>

                  {/* Gender — really optional */}
                  <div>
                    <label className="text-xs font-semibold text-stone-500 mb-1.5 block">
                      {t('Gender', '성별')} <span className="text-[10px] text-stone-400 font-normal">({t('optional', '선택')})</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {genderOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setGender(gender === opt.value ? '' : opt.value)}
                          className={`text-[11px] px-3 py-1.5 rounded-full border cursor-pointer transition-all whitespace-nowrap ${
                            gender === opt.value
                              ? 'bg-stone-800 text-white border-stone-800'
                              : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
                          }`}
                        >
                          {lang === 'ko' ? opt.ko : opt.en}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {authError && <p className="text-xs text-red-500">{authError}</p>}

              <button
                onClick={authTab === 'login' ? handleLogin : handleSignup}
                disabled={authLoading}
                className="w-full text-sm py-2.5 bg-stone-800 text-white rounded-lg hover:bg-stone-700 cursor-pointer transition-colors whitespace-nowrap font-semibold disabled:opacity-50"
              >
                {authLoading ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : authTab === 'login' ? (
                  t('Login', '로그인')
                ) : (
                  t('Create Account', '계정 만들기')
                )}
              </button>

              <button
                onClick={() => { setAuthModal(false); resetAuthForm(); }}
                className="w-full text-xs py-2 text-stone-400 hover:text-stone-600 cursor-pointer transition-colors"
              >
                {t('Cancel', '취소')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
